import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import '../../styles/settings.pcss'
import { Helmet } from 'react-helmet'
import Button from '../../components/ui/Button'
import {
  addWordFilter,
  fetchUserProfile,
  removeWordFilter,
} from '../../user/userActions'
import VoteFilter from './VoteFilter'
import {
  selectUserVoteFilterThreshold,
  selectUserWordFilters,
} from '../../selectors/userSelector'

const CHAR_FILTERS = [
  { char: '?', description: '(kysymysmerkki)' },
  { char: '!', description: '(huutomerkki)' },
  { char: '"', description: '(lainausmerkki)' },
  { char: '…', description: '(kolme pistettä)' },
]

/**
 * Same rules as in server profile.schemas.ts
 * @todo Move to pure server side validation to avoid keeping same rules in two places
 */
const validateFilterWord = (str) => {
  if (str.length < 2 || str.match(/^.\*$/)) {
    return 'Sanan tulee sisältää vähintään kaksi merkkiä.'
  }
  if (str.length > 84) {
    return 'Sanan tai lauseen tulee olla maksimissaan 84 merkkiä.'
  }
  if (str.match(/\*./)) {
    return 'Tähtimerkin "*" tulee sijaita sanan lopussa.'
  }
  if (str.includes('*') && str.includes(' ')) {
    return 'Tähtimerkin "*" käyttö on rajattu yksittäisiin sanoihin.'
  }
  if (str.includes('*') && str.includes('-')) {
    return 'Tähtimerkkiä "*" ei voi käyttää väliviivan sisältävien sanojen kanssa.'
  }
  if (str.split(' ').some((word) => word.length < 2)) {
    return 'Kaikkien sanojen tulee sisältää vähintään kaksi merkkiä.'
  }
  if (str.split(' ').some((word) => word.startsWith('-'))) {
    return 'Sana ei voi alkaa miinusmerkillä.'
  }
  const invalidCharacters = str.replace(/[ a-zA-Z0-9À-ÖØ-öø-ÿåÅČĆŽŠĐčćžšđ*-]/g, '')
  if (invalidCharacters.length > 0) {
    return 'Sana tai lause voi sisältää vain kirjaimia ja numeroita. Poista merkit: ' + invalidCharacters
  }
  return null
}

const WordFiltersView = () => {
  const dispatch = useDispatch()
  const wordFilters = useSelector(selectUserWordFilters)
  const voteFilterThreshold = useSelector(selectUserVoteFilterThreshold)

  const [inputText, setInputText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    dispatch(fetchUserProfile(5))
  }, [dispatch])

  const handleAdd = () => {
    const str = inputText.trim().replace(/\s+/g, ' ')
    if (!str) return

    const error = validateFilterWord(str)
    if (error) {
      setErrorMessage(error)
      return
    }

    if (wordFilters.size >= 40) {
      setErrorMessage('Sanasuodattimia voi olla enintään 40. Poista suodattimia lisätäksesi uuden.')
      return
    }

    setInputText('')
    setErrorMessage('')
    dispatch(addWordFilter(str))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  const handleRemove = (expression) => {
    dispatch(removeWordFilter(expression))
  }

  const handleCharFilterChange = (e) => {
    const char = e.target.value
    if (e.target.checked) {
      dispatch(addWordFilter(char))
    } else {
      dispatch(removeWordFilter(char))
    }
  }

  const filterWords = wordFilters.filter((expression) => expression.length > 1).toArray()

  return (
    <div>
      <Helmet title='Suodatus' />
      <section className='content'>
        <div className='personal word-filtering'>
          <div className='word-filters'>
            <h3>Uutisten sanasuodatus</h3>
            <p>
              Voit suodattaa uutisistasi tietyn sanan tai lauseen sisältävät otsikot pois. Lisää yksi sana tai lause kerrallaan.
              <br />
              Suodattimia voi olla enintään 40 kappaletta.
            </p>
            { !!errorMessage && <span className='settings-input-error'>{ errorMessage }</span> }
            <div className='filter-words'>
              <div className='input-wrapper'>
                <input
                  type='text'
                  value={ inputText }
                  onChange={ (e) => setInputText(e.target.value) }
                  onKeyDown={ handleKeyDown }
                  placeholder='Lisää sana tai lause'
                  aria-label='Suodatettava sana tai lause'
                />
                <Button type='button' text='Lisää' className='button_overlap-left' onClick={ handleAdd } />
              </div>
              <span className='description'>
                Sanan loppuosan voi korvata *-merkillä. Sanan minimimerkkimäärä on 2 ja sanan tai lauseen maksimimerkkimäärä on 84.
              </span>
              <div className='filter-words-list'>
                { filterWords.map((word) => (
                  <span key={ word } className='filter-words-list-item'>
                    { word }
                    <button
                      type='button'
                      className='fa fa-times filter-word-remove'
                      onClick={ () => handleRemove(word) }
                      title='Poista suodatin'
                      aria-label='Poista suodatin'
                    />
                  </span>
                )) }
              </div>
            </div>
          </div>
          <hr />
          <div className='char-filters'>
            <h3>Uutisten merkkisuodatus</h3>
            <p>Voit suodattaa uutisistasi tietyn merkin sisältävät otsikot.</p>
            <ul>
              { CHAR_FILTERS.map(({ char, description }) => {
                return (
                  <li key={ char }>
                    <label>
                      <input
                        type='checkbox'
                        value={ char }
                        checked={ wordFilters.contains(char) }
                        onChange={ handleCharFilterChange }
                      />
                      <span className='char-filter-char'>{ char }</span> { description }
                    </label>
                  </li>
                )
              }) }
            </ul>
          </div>
          <hr />
          <div className='votes-filter'>
            <h3>Negatiivisia ääniä saaneiden uutisten suodatus</h3>
            <p>Suodata pois mielestäsi liian monta miinusääntä saaneet uutisotsikot.</p>
            <VoteFilter dispatch={ dispatch } voteFilterThreshold={ voteFilterThreshold } />
          </div>
        </div>
      </section>
    </div>
  )
}

export default WordFiltersView
